import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { UserService } from "app/core/user/user.service";
import {
    BehaviorSubject,
    catchError,
    combineLatest,
    distinctUntilChanged,
    filter,
    map,
    Observable,
    of,
    shareReplay,
    throwError,
} from "rxjs";
import {
    ChangelogEntry,
    ChangelogListResponse,
    ChangelogRelease,
    ChangelogSection,
} from "../models/changelog.types";

const VALID_CATEGORIES = new Set(["feature", "improvement", "fix"]);

@Injectable({ providedIn: "root" })
export class ChangelogService {
    private readonly _dataUrl = "assets/changelog.json";
    private readonly _seenUrl = "/api/v1/changelog/seen";
    /**
     * Releases older than this window are hidden from users. They are still
     * kept in the JSON (so history is not lost at source) but the UI treats
     * them as stale. Change here to move the window for the whole app.
     */
    private readonly _visibilityDays = 45;

    private _releases$?: Observable<ChangelogRelease[]>;
    private _loadError = new BehaviorSubject<boolean>(false);
    // null while seen state is unknown for the current user; emits a Set
    // (possibly empty) once loaded. Subscribers wait via filter so the badge
    // does not flicker between "all unread" and the real count.
    private _seenIds$ = new BehaviorSubject<Set<string> | null>(null);

    readonly loadError$: Observable<boolean> = this._loadError.asObservable();

    constructor(
        private _http: HttpClient,
        private _userService: UserService
    ) {
        this._userService.user$
            .pipe(distinctUntilChanged((a, b) => a?.id === b?.id))
            .subscribe((user) => {
                if (user?.id) {
                    this._refreshSeenIds();
                } else {
                    this._seenIds$.next(new Set());
                }
            });
    }

    getReleases(): Observable<ChangelogRelease[]> {
        if (!this._releases$) {
            this._releases$ = this._http.get<ChangelogListResponse>(this._dataUrl).pipe(
                map((response) => this._validateAndSort(response)),
                catchError((err) => {
                    // eslint-disable-next-line no-console
                    console.error("[changelog] failed to load changelog.json", err);
                    this._loadError.next(true);
                    return of<ChangelogRelease[]>([]);
                }),
                shareReplay({ bufferSize: 1, refCount: false })
            );
        }
        return this._releases$;
    }

    getReleaseById(id: string): Observable<ChangelogRelease | undefined> {
        return this.getReleases().pipe(map((releases) => releases.find((release) => release.id === id)));
    }

    getSeenIds$(): Observable<Set<string>> {
        return this._seenIds$.pipe(filter((s): s is Set<string> => s !== null));
    }

    getLatestUnseenRelease(): Observable<ChangelogRelease | undefined> {
        return combineLatest([this.getReleases(), this.getSeenIds$()]).pipe(
            map(([releases, seen]) => releases.find((release) => !seen.has(release.id)))
        );
    }

    getUnreadCount(): Observable<number> {
        return combineLatest([this.getReleases(), this.getSeenIds$()]).pipe(
            map(([releases, seen]) => releases.filter((release) => !seen.has(release.id)).length)
        );
    }

    markAsSeen(releaseId: string): Observable<void> {
        const current = this._seenIds$.value ?? new Set<string>();
        if (current.has(releaseId)) {
            return of(void 0);
        }
        // Optimistic update so the UI reflects the change immediately; rolled
        // back below if the server rejects the write.
        const optimistic = new Set(current).add(releaseId);
        this._seenIds$.next(optimistic);
        return this._http.post<void>(`${this._seenUrl}/${releaseId}`, {}).pipe(
            catchError((err) => {
                this._seenIds$.next(current);
                // eslint-disable-next-line no-console
                console.warn(`[changelog] failed to mark '${releaseId}' as seen`, err);
                return throwError(() => err);
            })
        );
    }

    // -----------------------------------------------------------------------
    // Private
    // -----------------------------------------------------------------------

    private _refreshSeenIds(): void {
        this._http
            .get<{ seenReleaseIds: string[] }>(this._seenUrl)
            .pipe(
                map((r) => new Set<string>(Array.isArray(r?.seenReleaseIds) ? r.seenReleaseIds : [])),
                // 404 (non-admin) and other errors degrade to "nothing seen"
                // so the UI keeps working instead of breaking.
                catchError(() => of(new Set<string>()))
            )
            .subscribe((set) => this._seenIds$.next(set));
    }

    private _validateAndSort(response: ChangelogListResponse | null): ChangelogRelease[] {
        const raw = response?.releases;
        if (!Array.isArray(raw)) {
            // eslint-disable-next-line no-console
            console.warn("[changelog] invalid payload: releases is not an array");
            return [];
        }
        const cutoff = Date.now() - this._visibilityDays * 24 * 60 * 60 * 1000;
        const valid: ChangelogRelease[] = [];
        for (const release of raw) {
            const sanitized = this._sanitizeRelease(release);
            if (!sanitized) continue;
            if (new Date(sanitized.publishedAt).getTime() < cutoff) continue;
            valid.push(sanitized);
        }
        return valid.sort(
            (a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime()
        );
    }

    private _sanitizeRelease(release: unknown): ChangelogRelease | null {
        if (!release || typeof release !== "object") {
            return null;
        }
        const r = release as Record<string, unknown>;
        if (!isNonEmptyString(r.id) || !isNonEmptyString(r.version) || !isNonEmptyString(r.title)) {
            // eslint-disable-next-line no-console
            console.warn("[changelog] dropping release without id/version/title", r);
            return null;
        }
        if (typeof r.publishedAt !== "string" || Number.isNaN(new Date(r.publishedAt).getTime())) {
            // eslint-disable-next-line no-console
            console.warn(`[changelog] dropping release '${r.id}' with invalid publishedAt`);
            return null;
        }
        const sections = Array.isArray(r.sections)
            ? (r.sections
                  .map((s) => this._sanitizeSection(s, String(r.id)))
                  .filter(Boolean) as ChangelogSection[])
            : [];
        return {
            id: r.id,
            version: r.version,
            publishedAt: r.publishedAt,
            title: r.title,
            summary: typeof r.summary === "string" ? r.summary : "",
            sections,
        };
    }

    private _sanitizeSection(section: unknown, releaseId: string): ChangelogSection | null {
        if (!section || typeof section !== "object") {
            return null;
        }
        const s = section as Record<string, unknown>;
        if (typeof s.category !== "string" || !VALID_CATEGORIES.has(s.category)) {
            // eslint-disable-next-line no-console
            console.warn(`[changelog] release '${releaseId}': dropping section with invalid category '${s.category}'`);
            return null;
        }
        const entries = Array.isArray(s.entries)
            ? (s.entries
                  .map((e) => this._sanitizeEntry(e, releaseId))
                  .filter(Boolean) as ChangelogEntry[])
            : [];
        return {
            category: s.category as ChangelogSection["category"],
            title: typeof s.title === "string" ? s.title : "",
            entries,
        };
    }

    private _sanitizeEntry(entry: unknown, releaseId: string): ChangelogEntry | null {
        if (!entry || typeof entry !== "object") {
            return null;
        }
        const e = entry as Record<string, unknown>;
        if (!isNonEmptyString(e.id) || !isNonEmptyString(e.title)) {
            // eslint-disable-next-line no-console
            console.warn(`[changelog] release '${releaseId}': dropping entry without id/title`);
            return null;
        }
        const sanitized: ChangelogEntry = {
            id: e.id,
            title: e.title,
            description: typeof e.description === "string" ? e.description : "",
        };
        if (typeof e.location === "string") sanitized.location = e.location;
        if (typeof e.deepLink === "string" && e.deepLink.startsWith("/")) {
            sanitized.deepLink = e.deepLink;
        }
        if (Array.isArray(e.steps)) {
            const steps = e.steps.filter(isNonEmptyString);
            if (steps.length > 0) sanitized.steps = steps as string[];
        }
        if (e.media && typeof e.media === "object") {
            const m = e.media as Record<string, unknown>;
            if (typeof m.type === "string" && typeof m.url === "string") {
                const type =
                    m.type === "video" ? "video" : m.type === "gif" ? "gif" : "image";
                sanitized.media = {
                    type,
                    url: m.url,
                    alt: typeof m.alt === "string" ? m.alt : undefined,
                };
            }
        }
        return sanitized;
    }
}

function isNonEmptyString(v: unknown): v is string {
    return typeof v === "string" && v.length > 0;
}
